import { ApiError } from '../services/api';

/**
 * Traduit une ApiError (en particulier les erreurs de validation Zod du
 * backend, qui arrivent avec un tableau `issues`) en messages français
 * lisibles, orientés utilisateur.
 */

interface ZodIssue {
  path?: (string | number)[];
  message?: string;
  code?: string;
}

// Libellés FR des champs (alignés sur l'UI).
const FIELD_LABELS: Record<string, string> = {
  supplier_id: 'Fournisseur',
  zoho_vendor_id: 'Fournisseur Zoho',
  zoho_item_id: 'Produit Zoho',
  invoice_number: 'N° de facture',
  invoice_date: 'Date de facture',
  due_date: 'Échéance',
  charge_type: 'Type de charge',
  pedagogic_allocation: 'Affectation bilan pédagogique',
  payment_mode: 'Mode de paiement',
  bank: 'Banque',
  lines: 'Lignes',
  lignes: 'Lignes',
  valideur_email: 'Valideur',
  description: 'Description',
  quantity: 'Quantité',
  unit_price_ht: 'PU HT',
  vat_rate: 'TVA %',
  account: 'Compte',
  product: 'Produit',
  reason: 'Raison',
  email: 'Email',
  role: 'Rôle',
};

/** Construit l'emplacement lisible d'une issue ("Ligne 2 · Compte"). */
function describePath(path: (string | number)[] | undefined): string {
  if (!path || path.length === 0) return '';
  const parts: string[] = [];
  for (let i = 0; i < path.length; i++) {
    const seg = path[i];
    // Les deux orthographes du même concept : le flux factures nomme son
    // tableau `lines`, la soumission des paiements `lignes`. Sans les deux, le
    // numéro de la ligne fautive disparaît du message et l'utilisateur ne sait
    // pas laquelle corriger.
    if ((seg === 'lines' || seg === 'lignes') && typeof path[i + 1] === 'number') {
      parts.push(`Ligne ${(path[i + 1] as number) + 1}`);
      i++; // saute l'index numérique
    } else if (typeof seg === 'string') {
      parts.push(FIELD_LABELS[seg] ?? seg);
    }
  }
  return parts.join(' · ');
}

/** Message FR pour une issue, en fonction de son code Zod. */
function describeIssue(issue: ZodIssue): string {
  const where = describePath(issue.path);
  let what: string;
  switch (issue.code) {
    case 'invalid_type':
      what = 'champ obligatoire';
      break;
    case 'too_small':
      what = 'valeur requise';
      break;
    case 'unrecognized_keys':
      what = 'champ inattendu';
      break;
    case 'invalid_value':
    case 'invalid_enum_value':
      what = 'valeur non autorisée';
      break;
    default:
      // Conserve le message custom du backend (souvent déjà en FR, ex: format
      // de date) plutôt qu'un libellé générique.
      what = issue.message ?? 'invalide';
  }
  return where ? `${where} : ${what}` : what;
}

/**
 * Transforme une erreur d'appel API en message utilisateur.
 * - ApiError avec issues Zod → liste détaillée (1 ligne par champ fautif)
 * - ApiError simple → son message
 * - autre → String(err)
 */
export function formatApiError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; issues?: ZodIssue[] } | null;
    if (body?.issues && body.issues.length > 0) {
      const lines = body.issues.slice(0, 6).map(describeIssue);
      const more = body.issues.length > 6 ? `\n…et ${body.issues.length - 6} autre(s)` : '';
      return `Champs à corriger :\n${lines.map((l) => `• ${l}`).join('\n')}${more}`;
    }
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}
